import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { Referencia } from "@base/contracts";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  X,
} from "lucide-react";
import { Botao, Etiqueta, cn } from "@base/ui";
import { MidiaAutenticada } from "./midia-autenticada";
import { SecaoComentariosReferencia } from "./secao-comentarios-referencia";

type Slide = {
  readonly tipo: "image" | "video";
  readonly url: string;
};

/**
 * Viewer imersivo: ocupa quase toda a viewport.
 * Palco escuro para a mídia (aspecto real) + painel largo de detalhes.
 */
export function ModalDeReferencia({
  referencia,
  aoFechar,
}: {
  readonly referencia: Referencia;
  readonly aoFechar: () => void;
}) {
  const slides = useMemo(() => montarSlides(referencia), [referencia]);
  const [indice, definirIndice] = useState(0);
  const [proporcao, definirProporcao] = useState<number | null>(null);
  const slide = slides[indice] ?? null;

  useEffect(() => {
    definirIndice(0);
    definirProporcao(null);
  }, [referencia.id]);

  useEffect(() => {
    definirProporcao(null);
  }, [indice]);

  useEffect(() => {
    const tecla = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") aoFechar();
      if (evento.key === "ArrowLeft") {
        definirIndice((i) => Math.max(0, i - 1));
      }
      if (evento.key === "ArrowRight") {
        definirIndice((i) => Math.min(slides.length - 1, i + 1));
      }
    };
    document.addEventListener("keydown", tecla);
    const anterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", tecla);
      document.body.style.overflow = anterior;
    };
  }, [aoFechar, slides.length]);

  const username = referencia.username
    ? `@${referencia.username}`
    : "-";
  const analise = analiseDaPrescricao(referencia.prescricao);

  const ratio =
    proporcao ??
    (slide?.tipo === "video" ? 9 / 16 : 1);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-stretch justify-center p-0 sm:p-3 md:p-4"
      style={{ zIndex: "var(--rk-z-modal)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-referencia-titulo"
    >
      <button
        type="button"
        aria-label="Fechar"
        className="absolute inset-0 cursor-pointer bg-overlay backdrop-blur-[6px] transition-opacity duration-200"
        onClick={aoFechar}
      />

      <div
        className={cn(
          "relative z-10 flex h-full w-full max-w-[1600px] flex-col overflow-hidden",
          "bg-popover shadow-e3 animate-subir",
          "sm:rounded-2xl sm:border sm:border-border-subtle",
          "md:flex-row",
        )}
      >
        {/* Palco: ocupa todo o espaço restante */}
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col bg-black">
          <div className="relative flex min-h-0 flex-1 items-center justify-center p-2 sm:p-4 md:p-6">
            <div
              className="relative flex max-h-full max-w-full items-center justify-center"
              style={{
                aspectRatio: String(ratio),
                height: ratio <= 1.05 ? "100%" : "auto",
                width: ratio > 1.05 ? "100%" : "auto",
              }}
            >
              {slide ? (
                <MidiaAutenticada
                  src={slide.url}
                  tipo={slide.tipo}
                  controls={slide.tipo === "video"}
                  aoDimensoes={(largura, altura) => {
                    if (altura > 0) definirProporcao(largura / altura);
                  }}
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <p className="text-sm text-background/60">Sem mídia</p>
              )}
            </div>

            {slides.length > 1 ? (
              <>
                <button
                  type="button"
                  aria-label="Anterior"
                  disabled={indice === 0}
                  onClick={() => definirIndice((i) => Math.max(0, i - 1))}
                  className="absolute left-3 top-1/2 grid size-11 -translate-y-1/2 cursor-pointer place-items-center rounded-full border border-border/30 bg-background/95 text-foreground shadow-soft transition-opacity duration-200 hover:bg-background disabled:cursor-not-allowed disabled:opacity-30 md:left-5"
                >
                  <ChevronLeft className="size-5" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label="Próximo"
                  disabled={indice >= slides.length - 1}
                  onClick={() =>
                    definirIndice((i) => Math.min(slides.length - 1, i + 1))
                  }
                  className="absolute right-3 top-1/2 grid size-11 -translate-y-1/2 cursor-pointer place-items-center rounded-full border border-border/30 bg-background/95 text-foreground shadow-soft transition-opacity duration-200 hover:bg-background disabled:cursor-not-allowed disabled:opacity-30 md:right-5"
                >
                  <ChevronRight className="size-5" aria-hidden="true" />
                </button>
                <p className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-lg bg-background/95 px-3 py-1.5 text-xs font-medium tabular-nums text-foreground shadow-sm">
                  {indice + 1} / {slides.length}
                </p>
              </>
            ) : null}
          </div>
        </div>

        {/* Painel de detalhes */}
        <aside
          className={cn(
            "flex w-full shrink-0 flex-col border-t border-border bg-background",
            "max-h-[44dvh] md:max-h-none md:h-full",
            "md:w-[400px] lg:w-[440px] xl:w-[480px]",
            "md:border-l md:border-t-0",
          )}
        >
          <header className="flex items-start justify-between gap-3 border-b border-border px-5 py-5 lg:px-6">
            <div className="min-w-0 space-y-3">
              <p
                id="modal-referencia-titulo"
                className="truncate text-lg font-semibold tracking-tight text-foreground"
              >
                {username}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Etiqueta tom="contorno">{rotuloTipo(referencia.mediaType)}</Etiqueta>
                <Etiqueta
                  tom={referencia.isAd ? "atencao" : "sucesso"}
                >
                  {referencia.isAd ? "Anúncio" : "Orgânico"}
                </Etiqueta>
                <Etiqueta tom="primario" className="max-w-[16rem] truncate">
                  {referencia.collectionName}
                </Etiqueta>
              </div>
            </div>
            <button
              type="button"
              aria-label="Fechar modal"
              onClick={aoFechar}
              className="grid size-10 shrink-0 cursor-pointer place-items-center rounded-xl text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-foreground"
            >
              <X className="size-5" aria-hidden="true" />
            </button>
          </header>

          <div className="flex-1 space-y-7 overflow-auto px-5 py-6 lg:px-6">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <Stat
                icone={<Heart className="size-4" aria-hidden="true" />}
                valor={formatarNumero(referencia.likeCount)}
                rotulo="curtidas"
              />
              <Stat
                icone={<MessageCircle className="size-4" aria-hidden="true" />}
                valor={formatarNumero(referencia.commentCount)}
                rotulo="comentários"
              />
              <Stat
                icone={<Share2 className="size-4" aria-hidden="true" />}
                valor={formatarNumero(referencia.shareCount)}
                rotulo="compartilhamentos"
              />
              <Stat
                icone={<Bookmark className="size-4" aria-hidden="true" />}
                valor={formatarNumero(referencia.saveCount)}
                rotulo="salvamentos"
              />
            </div>

            <Secao titulo="Legenda">
              <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-foreground">
                {referencia.caption?.trim() || "Sem legenda"}
              </p>
            </Secao>

            <Secao titulo="Detalhes">
              <dl className="grid gap-3 text-sm">
                <Linha rotulo="Nome" valor={referencia.fullName?.trim() || "-"} />
                <Linha rotulo="Publicado" valor={formatarData(referencia.takenAt)} />
                <Linha
                  rotulo="Tipo"
                  valor={referencia.isAd ? "Anúncio" : "Orgânico"}
                />
              </dl>
            </Secao>

            {(referencia.tema ||
              referencia.gancho ||
              referencia.cta ||
              referencia.transcicao ||
              analise ||
              referencia.prescricao?.trim()) && (
              <Secao titulo="Prescrição">
                <div className="space-y-4 text-[15px] leading-relaxed text-foreground">
                  {referencia.tema?.trim() ? (
                    <Bloco rotulo="Tema" texto={referencia.tema} />
                  ) : null}
                  {referencia.gancho?.trim() ? (
                    <Bloco rotulo="Gancho" texto={referencia.gancho} />
                  ) : null}
                  {referencia.cta?.trim() ? (
                    <Bloco rotulo="CTA" texto={referencia.cta} />
                  ) : null}
                  {analise ? <Bloco rotulo="Análise" texto={analise} /> : null}
                  {!referencia.tema &&
                  !referencia.gancho &&
                  !referencia.cta &&
                  !referencia.transcicao &&
                  !analise &&
                  referencia.prescricao?.trim() ? (
                    <p className="whitespace-pre-wrap">
                      {referencia.prescricao.trim()}
                    </p>
                  ) : null}
                </div>
              </Secao>
            )}

            <Secao titulo="Transcrição">
              {referencia.transcicao?.trim() ? (
                <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-foreground">
                  {referencia.transcicao.trim()}
                </p>
              ) : (
                <div className="rounded-xl border border-dashed border-border/80 bg-muted/40 px-4 py-5">
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    Ainda sem transcrição. Em vídeos, isso é o que é falado no
                    áudio (traduzido para português quando precisa).
                  </p>
                </div>
              )}
            </Secao>

            {!referencia.tema &&
            !referencia.gancho &&
            !referencia.cta &&
            !referencia.transcicao &&
            !analise &&
            !referencia.prescricao?.trim() ? (
              <Secao titulo="Prescrição">
                <div className="rounded-xl border border-dashed border-border/80 bg-muted/40 px-4 py-5">
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    Ainda sem prescrição. No próximo sync com a IA ativa, este
                    bloco é preenchido com tema, gancho, CTA e transcrição.
                  </p>
                </div>
              </Secao>
            ) : null}

            <SecaoComentariosReferencia postId={referencia.id} />
          </div>

          <footer className="border-t border-border p-5 lg:p-6">
            {referencia.permalink ? (
              <Botao asChild className="w-full" variante="primario" tamanho="grande">
                <a
                  href={referencia.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="size-4" aria-hidden="true" />
                  Abrir no Instagram
                </a>
              </Botao>
            ) : (
              <p className="text-center text-xs text-muted-foreground">
                Sem link do Instagram neste post.
              </p>
            )}
          </footer>
        </aside>
      </div>
    </div>,
    document.body,
  );
}

function Stat({
  icone,
  valor,
  rotulo,
}: {
  readonly icone: ReactNode;
  readonly valor: string;
  readonly rotulo: string;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="text-muted-foreground">{icone}</span>
      <div className="min-w-0">
        <p className="text-base font-semibold tabular-nums tracking-tight text-foreground">
          {valor}
        </p>
        <p className="text-[11px] text-muted-foreground">{rotulo}</p>
      </div>
    </div>
  );
}

function Secao({
  titulo,
  children,
}: {
  readonly titulo: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h3 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {titulo}
      </h3>
      {children}
    </section>
  );
}

function Bloco({
  rotulo,
  texto,
}: {
  readonly rotulo: string;
  readonly texto: string;
}) {
  return (
    <div>
      <p className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {rotulo}
      </p>
      <p className="whitespace-pre-wrap">{texto.trim()}</p>
    </div>
  );
}

function Linha({
  rotulo,
  valor,
}: {
  readonly rotulo: string;
  readonly valor: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border/60 pb-3 last:border-0 last:pb-0">
      <dt className="shrink-0 text-muted-foreground">{rotulo}</dt>
      <dd className="truncate text-right font-medium text-foreground">{valor}</dd>
    </div>
  );
}

function montarSlides(referencia: Referencia): readonly Slide[] {
  if (referencia.mediaType === "video" && referencia.videoUrl) {
    return [{ tipo: "video", url: referencia.videoUrl }];
  }

  const imagens = referencia.images.length
    ? referencia.images
    : referencia.imageUrl
      ? [referencia.imageUrl]
      : [];

  if (imagens.length > 0) {
    return imagens.map((url) => ({ tipo: "image" as const, url }));
  }

  if (referencia.videoUrl) {
    return [{ tipo: "video", url: referencia.videoUrl }];
  }

  return [];
}

function rotuloTipo(tipo: Referencia["mediaType"]): string {
  switch (tipo) {
    case "image":
      return "Imagem";
    case "video":
      return "Vídeo";
    case "carousel":
      return "Carrossel";
    default:
      return "Desconhecido";
  }
}

function formatarNumero(valor: number | null): string {
  if (valor === null) return "-";
  return new Intl.NumberFormat("pt-BR").format(valor);
}

function formatarData(iso: string | null): string {
  if (!iso) return "-";
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return "-";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(data);
}

/** Extrai o bloco Análise da prescrição salva. */
function analiseDaPrescricao(prescricao: string | null): string | null {
  if (!prescricao?.trim()) return null;
  const inicio = /^Análise:\s*/im.exec(prescricao);
  if (!inicio) return null;
  const resto = prescricao.slice(inicio.index + inicio[0].length);
  const proximo =
    /\n(?:Tema|Gancho|CTA|Transcrição|Transcricao):/i.exec(resto);
  const valor = (proximo ? resto.slice(0, proximo.index) : resto).trim();
  if (!valor || valor === prescricao.trim()) return null;
  return valor;
}

/** Badge opcional no tile (não usado no modal). */
export function EtiquetaDeTipo({
  tipo,
}: {
  readonly tipo: Referencia["mediaType"];
}) {
  return <Etiqueta tom="contorno">{rotuloTipo(tipo)}</Etiqueta>;
}
